import { useState, type FormEvent } from 'react';
import Modal, { inputCls, labelCls } from '@/components/base/Modal';
import { saveCampaign, PLATFORM_LABELS } from '@/services/campaigns';

interface CampaignModalProps {
  onClose: () => void;
  onSaved: () => void;
}

const platformKeys = Object.keys(PLATFORM_LABELS);

export default function CampaignModal({ onClose, onSaved }: CampaignModalProps) {
  const [platform, setPlatform] = useState('meta_ads');
  const [campaignName, setCampaignName] = useState('');
  const [adsetName, setAdsetName] = useState('');
  const [adName, setAdName] = useState('');
  const [budgetLimit, setBudgetLimit] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    if (!campaignName.trim()) {
      setError('El nombre de la campaña es obligatorio.');
      return;
    }

    setSaving(true);
    const { error: saveError } = await saveCampaign({
      platform,
      campaignName,
      adsetName,
      adName,
      budgetLimit: Number(budgetLimit) || 0,
    });
    setSaving(false);

    if (saveError) {
      setError(saveError);
      return;
    }
    onSaved();
    onClose();
  }

  return (
    <Modal title="Nueva Campaña" subtitle="Registra una campaña publicitaria. El presupuesto es solo un tope de control." onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2.5">
            <i className="ri-error-warning-line"></i>
            <span>{error}</span>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Plataforma</label>
            <select className={inputCls} value={platform} onChange={(e) => setPlatform(e.target.value)}>
              {platformKeys.map((p) => (
                <option key={p} value={p}>{PLATFORM_LABELS[p]}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Presupuesto Tope / Límite ($)</label>
            <input type="number" min="0" className={inputCls} value={budgetLimit} onChange={(e) => setBudgetLimit(e.target.value)} placeholder="Ej: 500000" />
            <p className="text-xs text-foreground-400 mt-1">
              Es solo un tope de control. NO se suma a la inversión global. La inversión real se declara al pausar/finalizar la campaña.
            </p>
          </div>
        </div>
        <div>
          <label className={labelCls}>Nombre de campaña *</label>
          <input className={inputCls} value={campaignName} onChange={(e) => setCampaignName(e.target.value)} placeholder="Lanzamiento línea perros" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Conjunto de anuncios</label>
            <input className={inputCls} value={adsetName} onChange={(e) => setAdsetName(e.target.value)} placeholder="Audiencia Bogotá" />
          </div>
          <div>
            <label className={labelCls}>Anuncio</label>
            <input className={inputCls} value={adName} onChange={(e) => setAdName(e.target.value)} placeholder="Video carrusel" />
          </div>
        </div>

        <div className="flex gap-3 pt-1">
          <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 text-sm border border-background-300 text-foreground-700 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer">
            Cancelar
          </button>
          <button type="submit" disabled={saving} className="flex-1 px-4 py-2.5 text-sm font-semibold bg-accent-500 text-white rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed">
            {saving ? (<><i className="ri-loader-4-line animate-spin mr-1.5"></i>Guardando...</>) : (<><i className="ri-save-line mr-1.5"></i>Crear campaña</>)}
          </button>
        </div>
      </form>
    </Modal>
  );
}